/**
 * resolveArBundleDisplay
 *
 * The single display criteria for ArTable bundles: given the post-narrowing row
 * set it states, per bundle, what renders, why present members are unpainted,
 * the true member count behind the narrowing, and whether the bundle can
 * receive a dropped row. Pure — no React, no Valtio, no I/O.
 * `useArTableBundleRows` is a thin `useMemo` wrapper around this call;
 * `ArTableBundleRows`, `ArTableBundleContext`, `resolveArRowDrop` and
 * `ArTableBody.handleDragEnd` consume the result. Returns null when `rowsDocs`
 * is null or the page holds no bundles — the flat path derives its drag gate
 * from the same `drag` inputs.
 *
 * Dragging closes whenever `drag.rowsWithheld` (a split or column filter is
 * narrowing the table): `moveRowInManualOrder` bisects the LexoRank interval
 * between the dragged row's two RENDERED neighbours, and rows the narrowing
 * withheld between them keep their old ranks, so the row would land
 * arbitrarily once the narrowing clears. `appendOnly` is `handleDragEnd`'s old
 * `bundleHasRenderedMembers` test moved here: a bundle painting no members
 * offers no rendered gap to aim at, so a join must append. Collapse and the
 * view's hide flags are display-only — members stay in `memberRowIds`
 * (counts, selection, drag block intact) and are simply not painted.
 *
 * `memberCount` prefers the caller's whole-page census (`memberCountByBundleId`
 * from `rowsState.docs`), so the header badge quotes the members ON THIS PAGE
 * plus `withheldMemberCount` rather than the narrowed `memberRowIds.length`
 * alone (SYD-2406). A page census is all it is: `deleteBundle` and
 * `dissolveBundle` send a `{ bundleId }` scope the server expands over the whole
 * collection, and their confirmations quote no count at all (SYD-2430).
 *
 * A bundle cut by a pagination boundary is DESCRIBED, never imported (SYD-2430).
 * `offPageHeaders` supplies the header document of a bundle whose members start
 * this page headerless: it restores their `bundleType` — ending the fail-closed
 * blanking and the either-flag hiding — and mints one `bundleContinuation` item
 * at the first such member's position. That item carries no `rowId`, joins
 * neither `renderedRowIds` nor `bundleMap`, and creates no `BundleDraft`: the
 * off-page header is an annotation, not a row, and every `bundleMap` consumer
 * assumes an on-page `headerRowId`. `continuingBundleId` names the bundle a
 * page-turn probe proved continues past the last row, and marks
 * `continuesOnNextPage` on whichever item — header or continuation — owns it.
 */

import type { Snapshot } from 'valtio';
import type { ArBundleType, ArRowData, ArRowId } from '#types';
import type { DocEntry } from '../../../store/Store.types';
import { resolveBundleLabel } from '../bundleHeader';
import { classifyArBundleRows } from '../classifyArBundleRows';
import { canDragArTableRows } from './canDragArTableRows';
import type { ArTableRowDragState } from './canDragArTableRows';

type BundleHeaderItem = {
  kind: 'bundleHeader';
  rowId: ArRowId;
  bundleType: ArBundleType;
  memberRowIds: ArRowId[];
  continuesOnNextPage: boolean;
};

type BundleMemberItem = {
  kind: 'bundleMember';
  rowId: ArRowId;
  bundleId: string;
  bundleType: ArBundleType | undefined;
};

type UngroupedRowItem = {
  kind: 'ungrouped';
  rowId: ArRowId;
};

/**
 * The opening marker for a bundle whose header sits on the previous page. It
 * stands for no document, so it carries no `rowId` — that absence is the only
 * compile-time exhaustiveness guard this union has, and it is what makes every
 * consumer that would treat the item as a row a tsc error rather than a broken
 * render. Identity is `bundleId` (the off-page header's docId), which is also
 * the collapse key the real header uses.
 */
type BundleContinuationItem = {
  kind: 'bundleContinuation';
  bundleId: string;
  bundleType: ArBundleType;
  label: string;
  continuesOnNextPage: boolean;
};

type BundleRenderItem =
  | BundleHeaderItem
  | BundleMemberItem
  | UngroupedRowItem
  | BundleContinuationItem;

/**
 * Whether a bundle can receive a dropped row, with the machine-readable reason.
 * `'closed'` when dragging is off for the whole table — including
 * `rowsWithheld`, the rank-gap hazard described in the file JSDoc.
 * `'appendOnly'` when dragging is on but the bundle paints no members, so a
 * join must append (`afterRowId: null`) rather than aim at a rendered gap.
 */
type ArBundleDropAvailability =
  | { kind: 'open' }
  | { kind: 'appendOnly'; reason: 'collapsed' | 'viewHidden' | 'noMembers' }
  | { kind: 'closed'; reason: 'dragInactive' | 'rowsWithheld' };

type BundleMapEntry = {
  headerRowId: ArRowId;
  memberRowIds: ArRowId[];
  renderedMemberRowIds: ArRowId[];
  bundleType: ArBundleType;
  label: string;
  memberCount: number;
  withheldMemberCount: number;
  dropAvailability: ArBundleDropAvailability;
};

type ArBundleDisplay = {
  items: BundleRenderItem[];
  renderedRowIds: ArRowId[];
  bundleMap: Map<string, BundleMapEntry>;
};

type ResolveArBundleDisplayParams = {
  rowIds: readonly ArRowId[];
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>> | null;
  collapsedBundles: Readonly<Record<string, true>>;
  hideKitMembers: boolean;
  hideGroupMembers: boolean;
  drag: ArTableRowDragState;
  memberCountByBundleId?: ReadonlyMap<string, number>;
  /**
   * Header documents that live outside this page, keyed by bundleId (= the
   * header's docId). An entry resolves a member only when the bundle's header is
   * absent from the page WINDOW: `rowsDocs` is the whole window, while `rowIds`
   * is already narrowed by the split, the column filters and the PDF export
   * selection. Testing the window is what keeps a header the export left
   * unticked — or a filter withheld — from resolving its members and printing
   * them on a non-internal view, the fail-closed posture SYD-2279 chose. The
   * entry must also be a header claiming that same bundleId; anything else fails
   * closed exactly as an absent entry does. Pass a snapshot: the resolver runs
   * inside `useArTableBundleRows`' `useMemo`, so a bare proxy would not
   * subscribe and a bundle renamed on page 1 would leave page 2's continuation
   * label stale.
   */
  offPageHeaders?: ReadonlyMap<string, Snapshot<ArRowData>>;
  /**
   * The bundleId the page-turn probe proved continues past this page's last
   * row. `undefined` covers both "nothing continues" and "not yet known" — the
   * marker simply does not render.
   */
  continuingBundleId?: string;
};

type BundleDraft = {
  headerRowId: ArRowId;
  bundleType: ArBundleType;
  label: string;
  memberRowIds: ArRowId[];
  renderedMemberRowIds: ArRowId[];
  /**
   * Why this bundle's present members are unpainted — resolver-internal, and
   * the only thing that distinguishes one `appendOnly` reason from another. A
   * state description, not a member census: it is set even when the bundle has
   * no members on this page. `'view'` (the visibility view's hideKitComponents
   * / hideGroupComponents) wins over `'collapsed'` — expanding a collapse
   * cannot paint view-hidden members.
   */
  membersSuppressed: 'none' | 'collapsed' | 'view';
};

function resolveArBundleDisplay({
  rowIds,
  rowsDocs,
  collapsedBundles,
  hideKitMembers,
  hideGroupMembers,
  drag,
  memberCountByBundleId,
  offPageHeaders,
  continuingBundleId,
}: ResolveArBundleDisplayParams): ArBundleDisplay | null {
  if (!rowsDocs) return null;

  const classification = classifyArBundleRows({ rowIds, rowsDocs });
  if (!classification.hasBundles) return null;

  const canDragRows = canDragArTableRows(drag);
  const closedReason: 'dragInactive' | 'rowsWithheld' =
    !drag.manualOrderMode || drag.disabled ? 'dragInactive' : 'rowsWithheld';

  const items: BundleRenderItem[] = [];
  const renderedRowIds: ArRowId[] = [];
  const continuationEmitted = new Set<string>();
  const drafts = new Map<string, BundleDraft>();
  const headerItemByRowId = new Map<ArRowId, BundleHeaderItem>();

  /**
   * Headers are collected before any member is emitted: classification does not
   * depend on adjacency, so a member can precede its own header and would
   * otherwise be dropped from `memberRowIds`, silently shrinking the bundle's
   * count, selection and drag block. A bundleId is owned by its first header, so
   * a duplicate cannot make two rows share one `memberRowIds` array.
   */
  for (const row of classification.items) {
    if (row.kind !== 'bundleHeader') continue;
    const headerItem: BundleHeaderItem = {
      kind: 'bundleHeader',
      rowId: row.rowId,
      bundleType: row.bundleType,
      memberRowIds: [],
      continuesOnNextPage: row.bundleId === continuingBundleId,
    };
    headerItemByRowId.set(row.rowId, headerItem);
    if (drafts.has(row.bundleId)) continue;
    const viewHidden = row.bundleType === 'kit' ? hideKitMembers : hideGroupMembers;
    drafts.set(row.bundleId, {
      headerRowId: row.rowId,
      bundleType: row.bundleType,
      label: resolveBundleLabel({
        bundleValue: rowsDocs.get(row.rowId)?.data?.bundleValue,
        bundleType: row.bundleType,
        rowId: row.rowId,
      }),
      memberRowIds: headerItem.memberRowIds,
      renderedMemberRowIds: [],
      membersSuppressed: viewHidden
        ? 'view'
        : collapsedBundles[row.bundleId] === true
          ? 'collapsed'
          : 'none',
    });
  }

  for (const row of classification.items) {
    if (row.kind === 'bundleHeader') {
      items.push(headerItemByRowId.get(row.rowId)!);
      renderedRowIds.push(row.rowId);
      continue;
    }

    if (row.kind === 'bundleMember') {
      const bundle = drafts.get(row.bundleId);
      bundle?.memberRowIds.push(row.rowId);

      /**
       * An on-page header always wins, and so does one the narrowing withheld:
       * the classifier leaving `bundleType` unresolved only says "no header in
       * `rowIds`", so the gate tests the page window — `rowsDocs`, keyed by
       * docId, which IS the bundleId of the header. An entry whose document is
       * no longer a header, or whose `bundleId` diverges from the key it was
       * filed under, is ignored, and everything below then fails closed as it did
       * before `offPageHeaders` existed.
       */
      const offPageHeader = row.bundleType === undefined && !rowsDocs.has(row.bundleId)
        ? offPageHeaders?.get(row.bundleId)
        : undefined;
      const resolvedHeader = offPageHeader?.bundleRole === 'header'
        && offPageHeader.bundleId === row.bundleId
        ? offPageHeader
        : undefined;
      const bundleType = row.bundleType
        ?? (resolvedHeader ? resolvedHeader.bundleType ?? 'group' : undefined);

      /**
       * The continuation opens the run at the first member's position whether or
       * not that member paints, exactly as a header stays on screen over
       * suppressed members. Once per bundleId, so a column sort interleaving the
       * run cannot mint a second one.
       */
      if (resolvedHeader && !continuationEmitted.has(row.bundleId)) {
        continuationEmitted.add(row.bundleId);
        items.push({
          kind: 'bundleContinuation',
          bundleId: row.bundleId,
          bundleType: resolvedHeader.bundleType ?? 'group',
          label: resolveBundleLabel({
            bundleValue: resolvedHeader.bundleValue,
            bundleType: resolvedHeader.bundleType ?? 'group',
            rowId: row.bundleId as ArRowId,
          }),
          continuesOnNextPage: row.bundleId === continuingBundleId,
        });
      }

      /**
       * Collapse needs an expand affordance on screen, so a member whose header
       * is on neither this page nor `offPageHeaders` ignores it — the
       * continuation row is that affordance wherever it renders. An unresolved
       * bundle type still fails closed: it is a member, and neither hide flag
       * can be ruled out.
       */
      const isCollapsed = (bundle !== undefined || resolvedHeader !== undefined)
        && collapsedBundles[row.bundleId] === true;
      const isHiddenMember = bundleType === 'kit'
        ? hideKitMembers
        : bundleType === 'group'
          ? hideGroupMembers
          : hideKitMembers || hideGroupMembers;
      if (!isCollapsed && !isHiddenMember) {
        items.push({
          kind: 'bundleMember',
          rowId: row.rowId,
          bundleId: row.bundleId,
          bundleType,
        });
        renderedRowIds.push(row.rowId);
        bundle?.renderedMemberRowIds.push(row.rowId);
      }
      continue;
    }

    items.push({ kind: 'ungrouped', rowId: row.rowId });
    renderedRowIds.push(row.rowId);
  }

  const bundleMap = new Map<string, BundleMapEntry>();
  for (const [bundleId, draft] of drafts) {
    const memberCount = memberCountByBundleId?.get(bundleId) ?? draft.memberRowIds.length;
    const dropAvailability: ArBundleDropAvailability = !canDragRows
      ? { kind: 'closed', reason: closedReason }
      : draft.renderedMemberRowIds.length > 0
        ? { kind: 'open' }
        : {
          kind: 'appendOnly',
          reason: draft.memberRowIds.length === 0
            ? 'noMembers'
            : draft.membersSuppressed === 'view'
              ? 'viewHidden'
              : 'collapsed',
        };
    bundleMap.set(bundleId, {
      headerRowId: draft.headerRowId,
      memberRowIds: draft.memberRowIds,
      renderedMemberRowIds: draft.renderedMemberRowIds,
      bundleType: draft.bundleType,
      label: draft.label,
      memberCount,
      withheldMemberCount: Math.max(0, memberCount - draft.memberRowIds.length),
      dropAvailability,
    });
  }

  return { items, renderedRowIds, bundleMap };
}

export { resolveArBundleDisplay };
export type {
  ArBundleDisplay,
  ArBundleDropAvailability,
  BundleContinuationItem,
  BundleHeaderItem,
  BundleMapEntry,
  BundleMemberItem,
  BundleRenderItem,
  ResolveArBundleDisplayParams,
  UngroupedRowItem,
};
